import React, { useEffect, useState } from "react";
import { Table, Select, Input, Button, Space, Tooltip } from "antd";
import { CloseCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { ToastContainer, toast, Bounce } from "react-toastify";
import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import CategoryBadge from "../Commons/CategoryBadge";
import { getCategoryMeta } from "../../constants/activityCategories";
import "react-toastify/dist/ReactToastify.css";

const toIsoStartOfDay = (dateObject) => {
  if (!dateObject) return null;
  const d = dateObject instanceof DateObject ? dateObject.toDate() : new Date(dateObject);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const toIsoEndOfDay = (dateObject) => {
  if (!dateObject) return null;
  const d = dateObject instanceof DateObject ? dateObject.toDate() : new Date(dateObject);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};

const formatJalaliDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("fa-IR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const ActivityLogsGrid = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    category: undefined,
    adminName: "",
  });
  const [dateRange, setDateRange] = useState([]);

  const buildFilterString = (activeFilters = filters, range = dateRange) => {
    const parts = [];
    if (activeFilters.category) {
      parts.push(`Category=${activeFilters.category}`);
    }
    if (activeFilters.adminName?.trim()) {
      parts.push(`AdminName=*${activeFilters.adminName.trim()}`);
    }
    if (range?.[0]) {
      const from = toIsoStartOfDay(range[0]);
      if (from) parts.push(`CreatedAt>${from}`);
    }
    if (range?.[1]) {
      const to = toIsoEndOfDay(range[1]);
      if (to) parts.push(`CreatedAt<${to}`);
    } else if (range?.[0] && !range?.[1]) {
      const to = toIsoEndOfDay(range[0]);
      if (to) parts.push(`CreatedAt<${to}`);
    }
    return parts.join(",");
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/Logs/GetCategories`, {
        headers: { HeaderKey: config.HeaderKey },
        withCredentials: true,
      });
      const payload = response.data?.data ?? response.data;
      const list = Array.isArray(payload) ? payload : [];
      setCategories(list);
    } catch (err) {
      console.error("Fetch categories error:", err);
    }
  };

  const fetchLogs = async (
    page = 1,
    pageSize = pagination.pageSize,
    activeFilters = filters,
    range = dateRange
  ) => {
    setLoading(true);
    try {
      const requestBody = {
        Page: page,
        PageSize: pageSize,
        Filter: buildFilterString(activeFilters, range),
        OrderBy: "",
      };

      const response = await axios.post(
        `${config.apiBaseUrl}/api/Logs/GetList`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            HeaderKey: config.HeaderKey,
          },
          withCredentials: true,
        }
      );

      const payload = response.data?.data ?? response.data ?? {};
      const items = Array.isArray(payload.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      const total = payload.count ?? response.data?.count ?? items.length;

      setLogs(items);
      setPagination((prev) => ({ ...prev, total, current: page, pageSize }));
    } catch (err) {
      console.error("Fetch logs error:", err);
      toast.error(
        <p dir="rtl" className="font-custom">
          دریافت گزارش فعالیت‌ها با خطا مواجه شد
        </p>
      );
      if (err.response?.status === 401) {
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchLogs(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    fetchLogs(1, pagination.pageSize, filters, dateRange);
  };

  const clearFilters = () => {
    const cleared = { category: undefined, adminName: "" };
    setFilters(cleared);
    setDateRange([]);
    fetchLogs(1, pagination.pageSize, cleared, []);
  };

  const ClearSuffix = ({ onClick, visible }) => (
    <Tooltip title="پاک‌کردن">
      <CloseCircleOutlined
        onClick={onClick}
        style={{ cursor: "pointer", visibility: visible ? "visible" : "hidden" }}
        role="button"
        aria-label="clear"
      />
    </Tooltip>
  );

  const columns = [
    {
      title: "تاریخ",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      align: "center",
      className: "font-custom",
      render: (value) => formatJalaliDateTime(value),
    },
    {
      title: "دسته‌بندی",
      dataIndex: "category",
      key: "category",
      width: 140,
      align: "center",
      className: "font-custom",
      render: (category) => <CategoryBadge category={category} />,
    },
    {
      title: "ادمین",
      dataIndex: "adminName",
      key: "adminName",
      width: 180,
      align: "center",
      className: "font-custom",
      render: (name) => name || "—",
    },
    {
      title: "پیام",
      dataIndex: "message",
      key: "message",
      align: "right",
      className: "font-custom",
      render: (message) => (
        <Tooltip title={message} placement="topRight">
          <span dir="rtl" className="block text-right whitespace-normal break-words">
            {message}
          </span>
        </Tooltip>
      ),
    },
  ];

  const categoryOptions = categories.map((category) => ({
    value: category,
    label: getCategoryMeta(category).label,
  }));

  return (
    <div className="font-custom rtl text-right p-5">
      <h1 className="text-center text-xl mt-5 mb-5">گزارش فعالیت‌ها</h1>

      <div className="flex flex-wrap items-end gap-3 mb-5 justify-start">
        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-600">دسته‌بندی</label>
          <Select
            allowClear
            placeholder="همه"
            className="font-custom min-w-[180px]"
            value={filters.category}
            options={categoryOptions}
            onChange={(value) => setFilters((prev) => ({ ...prev, category: value }))}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-600">نام ادمین</label>
          <Input
            placeholder="جستجوی ادمین"
            className="font-custom min-w-[200px]"
            value={filters.adminName}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, adminName: e.target.value }))
            }
            onPressEnter={applyFilters}
            suffix={
              <ClearSuffix
                visible={!!filters.adminName}
                onClick={() => setFilters((prev) => ({ ...prev, adminName: "" }))}
              />
            }
            style={{ direction: "rtl" }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-600">بازه تاریخ</label>
          <DatePicker
            range
            calendar={persian}
            locale={persian_fa}
            value={dateRange}
            onChange={setDateRange}
            format="YYYY/MM/DD"
            inputClass="ant-input font-custom min-w-[220px] text-center"
            containerClassName="font-custom"
            placeholder="از تاریخ — تا تاریخ"
          />
        </div>

        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={applyFilters}
            className="font-custom"
          >
            جستجو
          </Button>
          <Button onClick={clearFilters} className="font-custom">
            پاک کردن فیلترها
          </Button>
        </Space>
      </div>

      <div className="w-full shadow-lg">
        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey={(record) => record.id}
          scroll={{ x: 900 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total, range) => `${range[0]}-${range[1]} از ${total}`,
            onChange: (page, pageSize) => {
              setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize,
              }));
              fetchLogs(page, pageSize);
            },
          }}
          bordered
        />
      </div>

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        className="font-custom text-center rtl"
        pauseOnHover
        theme="dark"
        transition={Bounce}
      />
    </div>
  );
};

export default ActivityLogsGrid;
